/**
 * authContext.js — React Context auth store.
 *
 * Exposes:
 *   accessToken, user, balance, isLoading, isAuthenticated
 *   login, register, logout
 *   fetchWallet()  — refresh balance from /api/v1/wallet
 *   placeBet()     — POST /api/v1/bets, refreshes wallet on success
 *   apiFetch       — pre-configured fetch wrapper with auto-refresh
 */

import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { configureApi, apiFetch } from './api';
import { API_BASE_URL as BASE_URL } from '../config';

function extractError(err, fallback = 'Une erreur est survenue.') {
  if (err instanceof TypeError && err.message === 'Failed to fetch') {
    return `Impossible de contacter le serveur. Vérifiez que le backend est démarré sur ${BASE_URL}`;
  }
  return err.message || fallback;
}

async function throwFromResponse(res, fallback) {
  let detail = fallback;
  try {
    const body = await res.json();
    if (typeof body.detail === 'string')      detail = body.detail;
    else if (Array.isArray(body.detail))      detail = body.detail.map((d) => (d.loc ? `${d.loc.slice(-1)[0]}: ${d.msg}` : d.msg)).join(' · ');
    else if (body.message)                    detail = body.message;
  } catch { /* keep fallback */ }
  throw new Error(`${res.status} — ${detail}`);
}

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [accessToken, setAccessToken] = useState(null);
  const [user, setUser]               = useState(null);
  const [balance, setBalance]         = useState(0);
  const [isLoading, setIsLoading]     = useState(true);

  const tokenRef = useRef(accessToken);
  tokenRef.current = accessToken;

  // Wire up the fetch utility once
  useEffect(() => {
    configureApi({
      getToken:      () => tokenRef.current,
      setToken:      (t) => { tokenRef.current = t; setAccessToken(t); },
      onAuthFailure: () => {
        setAccessToken(null);
        setUser(null);
        setBalance(0);
        window.location.href = '/login';
      },
    });
  }, []);

  /** GET /api/v1/auth/me */
  const fetchMe = useCallback(async (token) => {
    let res;
    try {
      res = await fetch(`${BASE_URL}/api/v1/auth/me`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      throw new Error(extractError(err, 'Impossible de récupérer le profil.'));
    }
    if (!res.ok) await throwFromResponse(res, 'Impossible de récupérer le profil.');
    return res.json();
  }, []);

  /** GET /api/v1/wallet — refresh balance */
  const fetchWallet = useCallback(async () => {
    const token = tokenRef.current;
    if (!token) return;
    try {
      const res = await fetch(`${BASE_URL}/api/v1/wallet`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return; // silent fail — balance stays as-is
      const data = await res.json();
      setBalance(parseFloat(data.balance) || 0);
    } catch { /* silent */ }
  }, []);

  /** Startup: silent refresh → me → wallet */
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/v1/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });

        if (!res.ok) {
          // No valid refresh cookie — expected when user was never logged in
          console.debug('[auth] No refresh cookie, user stays logged out');
          return;
        }

        const { access_token } = await res.json();
        tokenRef.current = access_token;
        setAccessToken(access_token);

        // Fetch /me and /wallet in parallel
        const [me, walletRes] = await Promise.all([
          fetchMe(access_token),
          fetch(`${BASE_URL}/api/v1/wallet`, {
            credentials: 'include',
            headers: { Authorization: `Bearer ${access_token}` },
          }),
        ]);

        setUser(me);

        if (walletRes.ok) {
          const w = await walletRes.json();
          setBalance(parseFloat(w.balance) || 0);
        }
      } catch (err) {
        // Log to console so you can see exactly what failed during development
        console.warn('[auth] Session restore failed:', err.message);
        // Don't touch accessToken/user — leave them as null (logged out)
      } finally {
        setIsLoading(false);
      }
    })();
  }, [fetchMe]);

  /** login(email, password) */
  const login = useCallback(async (email, password) => {
    const body = new URLSearchParams({ username: email, password });
    let res;
    try {
      res = await fetch(`${BASE_URL}/api/v1/auth/token`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
    } catch (err) {
      throw new Error(extractError(err, 'Connexion impossible.'));
    }
    if (!res.ok) await throwFromResponse(res, 'Email ou mot de passe incorrect.');
    const { access_token } = await res.json();
    tokenRef.current = access_token;
    setAccessToken(access_token);
    const me = await fetchMe(access_token);
    setUser(me);
    // Fetch wallet right after login
    try {
      const walletRes = await fetch(`${BASE_URL}/api/v1/wallet`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${access_token}` },
      });
      if (walletRes.ok) {
        const w = await walletRes.json();
        setBalance(parseFloat(w.balance) || 0);
      }
    } catch { /* silent */ }
    return me;
  }, [fetchMe]);

  /** register */
  const register = useCallback(async ({ username, email, password, phone_number, referral_code }) => {
    let res;
    try {
      res = await fetch(`${BASE_URL}/api/v1/auth/register`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username,
          email,
          password,
          ...(phone_number    ? { phone_number }    : {}),
          ...(referral_code   ? { referral_code }   : {}),
        }),
      });
    } catch (err) {
      throw new Error(extractError(err, 'Inscription impossible.'));
    }
    if (!res.ok) await throwFromResponse(res, "Échec de l'inscription.");
    return res.json();
  }, []);

  /** logout */
  const logout = useCallback(async () => {
    try {
      await fetch(`${BASE_URL}/api/v1/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch { /* best-effort */ }
    tokenRef.current = null;
    setAccessToken(null);
    setUser(null);
    setBalance(0);
  }, []);

  /**
   * placeBet({ stake, items: [{ fixture_id, odd_id }] })
   *
   * - Validates balance >= stake before calling the API
   * - Refreshes wallet on success
   * - Returns the created bet ticket on success
   * - Throws with a human-readable message on failure
   */
  const placeBet = useCallback(async ({ stake, items }) => {
    const token = tokenRef.current;
    if (!token) throw new Error('Vous devez être connecté pour parier.');

    if (balance < stake) {
      throw new Error('INSUFFICIENT_BALANCE');
    }

    let res;
    try {
      res = await fetch(`${BASE_URL}/api/v1/bets`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ stake, items }),
      });
    } catch (err) {
      throw new Error(extractError(err, 'Impossible de placer le pari.'));
    }

    if (!res.ok) await throwFromResponse(res, 'Échec du pari.');

    const ticket = await res.json();
    // Refresh balance immediately after bet (stake was deducted)
    await fetchWallet();
    return ticket;
  }, [balance, fetchWallet]);

  const value = {
    accessToken,
    user,
    balance,
    isLoading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    fetchWallet,
    placeBet,
    apiFetch,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
